/**
 * useAriaAnnouncer — React hook for debounced aria-live announcements.
 *
 * Provides a ref to attach to an aria-live region element and an `announce`
 * function that debounces rapid state changes to prevent screen reader spam.
 *
 * @see Requirements 10.3, 10.6
 */

import { useCallback, useEffect, useRef } from 'react';
import { createDebouncedAnnouncer, type DebouncedAnnouncer } from '../utils/accessibility';

interface UseAriaAnnouncerOptions {
  /** Debounce delay in milliseconds. Default: 300ms */
  debounceMs?: number;
}

interface UseAriaAnnouncerReturn {
  /** Ref to attach to the aria-live region element */
  regionRef: React.RefCallback<HTMLElement>;
  /** Queue a debounced announcement */
  announce: (message: string) => void;
  /** Clear any pending announcement */
  clear: () => void;
}

/**
 * Hook that manages a debounced aria-live announcer.
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const { regionRef, announce } = useAriaAnnouncer({ debounceMs: 300 });
 *
 *   useEffect(() => {
 *     announce(`${count} results found`);
 *   }, [count, announce]);
 *
 *   return (
 *     <>
 *       {/* ... component content ... *\/}
 *       <div ref={regionRef} aria-live="polite" aria-atomic="true" className="sr-only" />
 *     </>
 *   );
 * }
 * ```
 */
export function useAriaAnnouncer(
  options: UseAriaAnnouncerOptions = {},
): UseAriaAnnouncerReturn {
  const { debounceMs = 300 } = options;
  const announcerRef = useRef<DebouncedAnnouncer | null>(null);

  // Create the announcer once
  if (!announcerRef.current) {
    announcerRef.current = createDebouncedAnnouncer(debounceMs);
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      announcerRef.current?.destroy();
    };
  }, []);

  // Ref callback to wire up the DOM element
  const regionRef = useCallback((node: HTMLElement | null) => {
    announcerRef.current?.setTarget(node);
  }, []);

  const announce = useCallback((message: string) => {
    announcerRef.current?.announce(message);
  }, []);

  const clear = useCallback(() => {
    announcerRef.current?.clear();
  }, []);

  return { regionRef, announce, clear };
}
